import React, { useState, useEffect, useRef } from 'react';
import { Search, User, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

function PatientSearchInput({ patients, selectedPatient, onPatientSelect, onPatientClear,isEditMode,editInvoiceData,setSelectedPatient }) {
  const [patientSearchTerm, setPatientSearchTerm] = useState('');
  const [showPatientDropdown, setShowPatientDropdown] = useState(false);
  const containerRef = useRef(null);
  const navigate = useNavigate();

  // Update search term when patient is selected externally
  useEffect(() => {
    if (selectedPatient) {
      setPatientSearchTerm(selectedPatient.name || selectedPatient.patientName || '');
    } else {
      setPatientSearchTerm('');
    }
  }, [selectedPatient]);

  // Handle outside clicks
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setShowPatientDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Filter patients based on search term
  const filteredPatients = () => {
    if (!patientSearchTerm) return patients;

    const searchTerm = patientSearchTerm.toLowerCase();
    return patients.filter((patient) =>
      [
        patient.patientName,
        patient.patientIDUniqueID,
        patient.appointmentID,
        patient.age?.toString(),
        patient.gender,
      ]
        .filter(Boolean)
        .some((field) => field.toString().toLowerCase().includes(searchTerm))
    );
  };
  
useEffect(() => {
  if (isEditMode && editInvoiceData?.clinicAndPatientInfo) {
    const p = editInvoiceData.clinicAndPatientInfo;
    const formattedPatient = {
      name: p.patientName,
      patientName: p.patientName,
      age: p.age,
      gender: p.gender,
      phone: p.patientContactNumber,
      patientId: p.patientIDUniqueID,
      patientUserUniqueID: p.patientUserUniqueID,
      appointmentID: p.appointmentID,
      allergies: p.allergies_ifany
        ? p.allergies_ifany.split(",").map((i) => i.trim())
        : [],
      conditions: p.medicalConditions || "",
      address: p.address || "",
      dateOfBirth: p.dateOfBirth || "",
    };
    setSelectedPatient(formattedPatient);
  }
}, [isEditMode, editInvoiceData]);

  const selectPatient = (patient) => {
    const formattedPatient = {
      name: patient.patientName,
      patientName: patient.patientName, // Keep both for compatibility
      age: patient.age,
      gender: patient.gender,
      phone: patient.patientContactNumber,
      patientId: patient.patientIDUniqueID,
       patientUserUniqueID: patient.patientUserUniqueID, 
    appointmentId: patient.appointmentID, 
      allergies: patient.allergies_ifany
        ? patient.allergies_ifany.split(',').map((item) => item.trim())
        : [],
      conditions: patient.medicalConditions,
      address: patient.address || '',
      dateOfBirth: patient.dateOfBirth || '',
    };

    onPatientSelect(formattedPatient);
    setPatientSearchTerm(`${patient.patientName}`); 
  setShowPatientDropdown(false);
  };

  const clearPatientSelection = () => {
    onPatientClear();
    setPatientSearchTerm('');
  };

  return (
    <div
      style={{
        background: 'white',
        borderRadius: '20px',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
        border: '1px solid #f3f4f6',
        padding: '20px',
        marginBottom: '24px',
        maxHeight: '296px',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          marginBottom: '16px',
        }}
      >
        <div
          style={{
            width: '36px',
            height: '36px',
            background: 'linear-gradient(135deg, #00161f, #203342, #3A6073)',
            borderRadius: '10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <User style={{ width: '18px', height: '18px', color: 'white' }} />
        </div>
        <h3
          style={{
            fontSize: '16px',
            fontWeight: '600',
            color: '#1f2937',
          }}
        >
          Patient Information
        </h3>
      </div>

      {/* Patient Search Input */}
     {/* Only show search input when NOT editing */}
{!isEditMode && (
  <div ref={containerRef} className="relative mb-2">
    <div className="relative w-full">
      <input
        type="text"
        placeholder="Search patient by name, ID, or phone..."
        value={patientSearchTerm}
        onChange={(e) => {
          setPatientSearchTerm(e.target.value);
          setShowPatientDropdown(true);
          if (!e.target.value) onPatientClear();
        }}
        onFocus={() => setShowPatientDropdown(true)}
        className="w-full px-3 py-2.5 pr-9 bg-gray-50 border border-gray-200 rounded-lg 
                 text-sm outline-none transition-all duration-200
                 focus:border-slate-600 focus:bg-white focus:ring-2 focus:ring-slate-100"
      />
      <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
        <Search className="w-4 h-4 text-gray-400" />
      </div>
    </div>

    {/* Dropdown */}
    {showPatientDropdown && (
      <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl z-50 max-h-44 overflow-y-auto">
        {(patientSearchTerm ? filteredPatients() : patients).length > 0 ? (
          (patientSearchTerm ? filteredPatients() : patients).map((patient) => (
            <div
              key={`${patient.patientIDUniqueID}-${patient.appointmentID}`}
              onClick={() => selectPatient(patient)}
              className="p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors"
            >
              <div className="font-medium text-sm text-gray-800 mb-0.5">
                {patient.patientName}
              </div>
              <div className="text-xs text-gray-500">
                {patient.patientIDUniqueID} • {patient.age}y • {patient.gender} • AppointmentID: {patient.appointmentID}
              </div>
            </div>
          ))
        ) : (
          <div className="p-3 text-center text-sm text-gray-500">No patients found</div>
        )}
      </div>
    )}
  </div>
)}


      {/* Selected Patient Display */}
      {selectedPatient ? (
        <div
          style={{
            border: '1.5px solid #3A6073',
            borderRadius: '10px',
            padding: '12px',
            background: 'linear-gradient(135deg, #f0fdf8 0%, #e8faf5 100%)',
            maxHeight: '160px',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              marginBottom: '10px',
            }}
          >
            <div style={{ flex: 1 }}>
              <h4
                style={{
                  fontSize: '15px',
                  fontWeight: '600',
                  color: '#1f2937',
                  marginBottom: '3px',
                  lineHeight: '1.3',
                }}
              >
                {selectedPatient.name || selectedPatient.patientName}
              </h4>
              <div
                style={{
                  fontSize: '12px',
                  color: '#6b7280',
                  lineHeight: '1.4',
                }}
              >
                {selectedPatient.age} • {selectedPatient.gender} • {selectedPatient.appointmentID}
              </div>
            </div>
            <button
              onClick={clearPatientSelection}
              style={{
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                color: '#9ca3af',
                padding: '2px',
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              onMouseEnter={(e) => {
                e.target.style.background = 'rgba(239, 68, 68, 0.1)';
                e.target.style.color = '#ef4444';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'transparent';
                e.target.style.color = '#9ca3af';
              }}
            >
              <X style={{ width: '14px', height: '14px' }} />
            </button>
          </div>

          <div style={{ display: 'grid', gap: '8px' }}>
            <div>
              <span
                style={{
                  fontSize: '11px',
                  fontWeight: '600',
                  color: '#374151',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}
              >
                Allergies:
              </span>
              <span
                style={{
                  fontSize: '12px',
                  color: '#6b7280',
                  marginLeft: '6px',
                }}
              >
                {selectedPatient.allergies?.length
                  ? selectedPatient.allergies.join(', ')
                  : 'None'}
              </span>
            </div>
            <div>
              <span
                style={{
                  fontSize: '11px',
                  fontWeight: '600',
                  color: '#374151',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}
              >
                Conditions:
              </span>
              <span
                style={{
                  fontSize: '12px',
                  color: '#6b7280',
                  marginLeft: '6px',
                }}
              >
                {selectedPatient.conditions || 'None'}
              </span>
            </div>
          </div>

          {/* <button
           onClick={()=>navigate(`/ClinicPatientView/${selectedPatient?.patientUserUniqueID}`)}
            style={{
              color: '#3A6073',
              background: 'transparent',
              border: 'none',
              fontSize: '12px',
              fontWeight: '500',
              cursor: 'pointer',
              textDecoration: 'underline',
              padding: '0',
              marginTop: '10px',
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => {
              e.target.style.color = '#2d4a59';
            }}
            onMouseLeave={(e) => {
              e.target.style.color = '#3A6073';
            }}
          >
            View patient details →
          </button> */}
        </div>
      ) : (
        <div
          style={{
            textAlign: 'center',
            padding: '20px 12px',
            color: '#9ca3af',
          }}
        >
          <div
            style={{
              width: '40px',
              height: '40px',
              background: '#f3f4f6',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 8px',
            }}
          >
            <User style={{ width: '20px', height: '20px' }} />
          </div>
          <p
            style={{
              fontSize: '13px',
              marginBottom: '2px',
              fontWeight: '500',
            }}
          >
            No patient selected
          </p>
          <p style={{ fontSize: '11px', color: '#9ca3af' }}>
            Click above to search and select
          </p>
        </div>
      )}
    </div>
  );
}

export default PatientSearchInput;
